import { differenceInMinutes } from 'date-fns';
import { Institution } from '~/types/institution';
import {
  BeneficiaryCheckStatus,
  BeneficiaryMatchStatus,
  Payment,
  PaymentBeneficiaryCheckResponse,
  PaymentsFiltersFields,
  PaymentStatus,
} from '~/types/payment';
import { isSwanInstitution } from './institutions';

export function getRecipientValidationRegex(institution?: Institution | null): RegExp | null {
  if (!institution || isSwanInstitution(institution)) return null;
  return new RegExp('[^0-9a-zA-Z\\s]', 'gi');
}

export const sanitizePayeeName = (payeeName: string, institution?: Institution | null): string => {
  const invalidCharRegex = getRecipientValidationRegex(institution);
  if (!invalidCharRegex) return payeeName;

  return payeeName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics (combining marks): è -> e
    .replace(/&/g, ' and ') // ampersand -> the word "and"
    .replace(invalidCharRegex, ' ') // any other char the institution rejects -> space
    .replace(/\s+/g, ' ') // collapse whitespace runs
    .trim()
    .slice(0, PAYMENT_RECIPIENT_MAX_LENGTH); // truncate to max length
};

export const PAYMENT_RECIPIENT_MAX_LENGTH = 70;
export const SEPA_DESCRIPTION_MAX_LENGTH = 70;
// SEPA Ustrd max length (ISO 20022).
export const SEPA_REMITTANCE_MESSAGE_MAX_LENGTH = 140;

export function getPaymentReasonMaxLength(institution?: Institution | null): number {
  if (!institution) return 44;
  return isSwanInstitution(institution) ? 140 : 44;
}

export const isPaymentInProgress = (payment: Payment) => {
  return payment.status === PaymentStatus.Accepted;
};

export const moreThan5MinutesAfterCreation = (payment: Payment) =>
  differenceInMinutes(new Date(), new Date(payment.createdAt)) > 5;

export const isPaymentLikelyFailed = (payment: Payment) => {
  if (payment.status === PaymentStatus.Failed) return true;
  if (payment.status === PaymentStatus.Pending && moreThan5MinutesAfterCreation(payment)) return true;
  return false;
};

export const isPaymentSucceeded = (payment: Payment) => payment.status === PaymentStatus.Succeeded;
export const isPaymentCanceled = (payment: Payment) => payment.status === PaymentStatus.Canceled;
export const isPaymentTimeout = (payment: Payment) => payment.status === PaymentStatus.Timeout;

export const last30MinutesPendingPaymentsFilters = (debouncedInput: string) => {
  const startDate = new Date(new Date().getTime() - 30 * 60 * 1000);
  startDate.setSeconds(0, 0);
  const filters: PaymentsFiltersFields = {
    search: debouncedInput,
    status: [PaymentStatus.Pending],
    dateRange: [startDate.toISOString(), null],
  };
  return filters;
};

export const canChangePaymentStatus = (payment: Payment) =>
  payment.status === PaymentStatus.Pending ||
  payment.metadata?.reached_timeout === true ||
  payment.metadata?.changed_status_from_pending === true;

type PaymentBeneficiaryCheckWithApprovalNeeded = Omit<PaymentBeneficiaryCheckResponse, 'matchStatus'> & {
  matchStatus:
    | BeneficiaryMatchStatus.CloseMatch
    | BeneficiaryMatchStatus.NoMatch
    | BeneficiaryMatchStatus.VerificationCheckNotPossible;
};

export const paymentBeneficiaryNeedsApproval = (
  beneficiaryCheck: PaymentBeneficiaryCheckResponse,
): beneficiaryCheck is PaymentBeneficiaryCheckWithApprovalNeeded => {
  return Boolean(
    ![BeneficiaryMatchStatus.Exempted, BeneficiaryMatchStatus.FullMatch].includes(beneficiaryCheck.matchStatus) &&
    beneficiaryCheck.status === BeneficiaryCheckStatus.Pending,
  );
};

export function assertPaymentBeneficiaryNeedsApproval(
  beneficiaryCheck: PaymentBeneficiaryCheckResponse,
): asserts beneficiaryCheck is PaymentBeneficiaryCheckWithApprovalNeeded {
  if (!paymentBeneficiaryNeedsApproval(beneficiaryCheck)) {
    throw new Error('Payment beneficiary does not need approval');
  }
}

export const isToBeVerifiedStatusFilter = (status: PaymentStatus[]) =>
  status.length === 2 && status.every((s) => [PaymentStatus.Timeout, PaymentStatus.Pending].includes(s));

export const getActiveStatusTranslationKey = (filters: PaymentsFiltersFields) => {
  const { status } = filters;
  if (isToBeVerifiedStatusFilter(status)) {
    return 'status_labels.to_be_verified' as const;
  }
  if (status.length === 1) {
    switch (status[0]) {
      case PaymentStatus.Accepted:
        return 'status_labels.accepted' as const;
      case PaymentStatus.Succeeded:
        return 'status_labels.succeeded' as const;
      case PaymentStatus.Failed:
        return 'status_labels.failed' as const;
      case PaymentStatus.Scheduled:
        return 'status_labels.scheduled' as const;
    }
  }
  return null;
};
